import groq from 'groq';
import { LegacyBannerType, BlockType, ObjectType, ModuleType } from '@dap/sanity/types';
import { proffModuleQuery } from './moduleQueries';
import { customLinkQuery } from './fieldQueries';
import { personQuery } from './documentQueries';

export const newsListBlockQuery = groq`
  title,
  limit,
  tags,
  articles
`;

export const eventListBlockQuery = groq`
  title,
  source,
  maxItems
`;

export const legacyBannerBlockQuery = groq`
  variant[]{
    _type,
    _key,
    _type == "${LegacyBannerType.Image}" => {
      "image": image.asset->
    },
    _type == "${LegacyBannerType.MGA}" => {
      "order_module": *[
        _type == "${ModuleType.orderModule}"
        && coalesce(^.^.^.^.brand, ^.^.^.brand) in brand
        && show
      ][0]{
        "slug": module_slug.current
      }
    },
    // Fetches the first referenced proff module from the brand configuration
    // NOTE - This assumes there is only added one proff module to the brand configuration
    _type == "legacyProffBanner" => {
      "proff_module": *[
        _type == "brandConfiguration"
        && coalesce(^.^.^.^.brand, ^.^.^.brand) == brand
        && !(_id in path("drafts.*"))
      ][]{
        "proffModuleData": modules[_type match "proff_module"][0]->{
          show,
          ${proffModuleQuery}
        }
      }[proffModuleData.show][0]{ // Ensure we only fetch the module if it's visible
          ...
      }.proffModuleData
    }
  }
`;

export const featuredArticleQuery = groq`
  title,
  intro,
  "slug": url.current,
  brands,
  image {
    asset->{
      _id,
      metadata {
        dimensions
      }
    },
    crop,
    hotspot
  }
`;

export const featuredArticleBlockQuery = groq`
  article->{
    ${featuredArticleQuery}
  },
  imagePlacement,
  title,
  intro,
  image {
    asset->{
      _id,
      metadata {
        dimensions
      }
    },
    crop,
    hotspot
  }
`;

export const featuredSubjectArticleBlockQuery = groq`
  article->{
    ${featuredArticleQuery},
    subject->{
      "slug": slug.current
    }
  },
  imagePlacement,
  title,
  intro,
  image {
    asset->{
      _id,
      metadata {
        dimensions
      }
    },
    crop,
    hotspot
  }
`;

export const serviceMessageBlockQuery = groq`
  severity,
  title,
  line_1,
  line_2
`;

export const linkListBlockQuery = groq`
  title,
  palette,
  links[]{
    _key,
    title,
    subtitle,
    "link": link[0]{
      ${customLinkQuery}
    }
  }
`;

export const personListBlockQuery = groq`
  title,
  people[]->{
    ${personQuery}
  }
`;

export const richTextBlockQuery = groq`
  title,
  body[]{
    ...,
    markDefs[]{
      ...,
      _type == "${ObjectType.CustomLink}" => {
        link[0]{
          ${customLinkQuery}
        }
      },
    }
  }
`;

export const textBlockQuery = groq`
  title,
  palette,
  callToAction {
    linkText,
    link[0]{
      ${customLinkQuery}
    }
  },
  textBlock[]{
    ...,
    markDefs[]{
      ...,
      _type == "${ObjectType.CustomLink}" => {
        link[0]{
          ${customLinkQuery}
        }
      },
    }
  }
`;

export const videoBlockQuery = groq`
  title,
  vimeoId,
  poster,
  muted,
  loop,
  autoPlay
`;

export const imageCarouselBlockQuery = groq`
  allowFullScreen,
  showCaptions,
  images[]{
    "image": image {
      ...,
      ...asset-> {
        altText,
        caption,
        ...metadata {
          lqip, // the lqip can be used for blurHashUrl or other low-quality placeholders
          ...dimensions {
            width,
            height
          }
        },
        url
      }
    },
    caption,
    altText
  }
`;

export const socialFeedBlockQuery = groq`
  socialFeed->{
    _id,
    title,
    emoji,
    "slug": slug.current
  },
  numberOfPosts
`;

export const contentBlocksQuery = groq`
  _type,
  _key,
  _type == "${BlockType.EventList}" => {
    ${eventListBlockQuery}
  },
  _type == "${BlockType.FeaturedArticle}" => {
    ${featuredArticleBlockQuery}
  },
  _type == "${BlockType.FeaturedSubjectArticle}" => {
    ${featuredSubjectArticleBlockQuery}
  },
  _type == "${BlockType.ImageCarousel}" => {
    ${imageCarouselBlockQuery}
  },
  _type == "${BlockType.Leads}" => {},
  _type == "${BlockType.LegacyBanner}" => {
    ${legacyBannerBlockQuery}
  },
  _type == "${BlockType.LinkList}" => {
    ${linkListBlockQuery}
  },
  _type == "${BlockType.NewsList}" => {
    ${newsListBlockQuery}
  },
  _type == "${BlockType.PersonList}" => {
    ${personListBlockQuery}
  },
  _type == "${BlockType.RichText}" => {
    ${richTextBlockQuery}
  },
  _type == "${BlockType.ServiceMessage}" => {
    ${serviceMessageBlockQuery}
  },
  _type == "${BlockType.SocialFeed}" => {
    ${socialFeedBlockQuery}
  },
  _type == "${BlockType.SubjectLinkList}" => {
    ${linkListBlockQuery}
  },
  _type == "${BlockType.SubjectText}" => {
    ${textBlockQuery}
  },
  _type == "${BlockType.Text}" => {
    ${textBlockQuery}
  },
  _type == "${BlockType.Video}" => {
    ${videoBlockQuery}
  },
  _type == "${BlockType.WorkplaceFeed}" => {}
`;
